import React from "react";
import { useIntl, defineMessages } from "react-intl";

import { Heading } from "@labxchange/ui-components/lib/Heading";
import { Label } from "@labxchange/ui-components/lib/Label";
import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";

import notebookGrid from "./assets/notebook-grid.svg";
import ManualFooter from "./assets/manual-footer.svg";
import ManualHeader from "./assets/manual-header.svg";
import notebookNextButton from "./assets/notebookNextButton.svg";
import { Content } from "./Content";
import * as styles from "./NotebookManual.css";
import { NotebookManualProps } from "./types";
import { fetchTranslations } from "./locales/config";

const messages = defineMessages({
  titleLabel: {
    id: "NotebookManual.titleLabel",
    defaultMessage: "Title",
    description: "Label for the title field in the notebook manual header",
  },
  dateLabel: {
    id: "NotebookManual.dateLabel",
    defaultMessage: "Date",
    description: "Label for the date field in the notebook manual header",
  },
  previousPage: {
    id: "NotebookManual.previousPage",
    defaultMessage: "Previous Page",
    description: "Alt text for the previous page navigation button",
  },
  nextPage: {
    id: "NotebookManual.nextPage",
    defaultMessage: "Next Page",
    description: "Alt text for the next page navigation button",
  },
});

/**
 * NotebookManual:
 *  - Dynamic component that renders the NotebookManual
 */
function NotebookManualInternal({
  pages,
  manualSize = "small",
  showNotebookGridBody = false,
  displayPageNum = true,
  displayPageTitle = true,
  displayDate = true,
}: NotebookManualProps) {
  const intl = useIntl();
  const [currentPageIndex, setCurrentPageIndex] = React.useState(0);
  const [showFooter, setShowFooter] = React.useState(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const handlePageNext = () => {
    if (currentPageIndex < (pages?.length || 0) - 1) {
      setCurrentPageIndex(currentPageIndex + 1);
    }
  };

  const handlePagePrevious = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
    }
  };

  const checkScrollPosition = React.useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const { scrollHeight, clientHeight } = scrollElement;
    const hasOverflow = scrollHeight > clientHeight;

    if (!hasOverflow) {
      setShowFooter(false);
    } else {
      setShowFooter(true);
    }
  }, [manualSize]);

  React.useEffect(() => {
    checkScrollPosition();

    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", checkScrollPosition);
      window.addEventListener("resize", checkScrollPosition);

      return () => {
        scrollElement.removeEventListener("scroll", checkScrollPosition);
        window.removeEventListener("resize", checkScrollPosition);
      };
    }

    return () => {};
  }, [checkScrollPosition, currentPageIndex]);

  const currentPage = pages?.[currentPageIndex];

  return (
    <div
      className={`${styles.manual} ${manualSize === "medium" ? styles.manualMedium : ""} ${
        manualSize === "small" ? styles.manualSmall : ""
      }`}
    >
      <img src={ManualHeader} alt="" />
      <div
        className={`${styles.body} ${
          manualSize === "small"
            ? styles.manualSmallBody
            : manualSize === "medium"
              ? styles.manualMediumBody
              : ""
        } ${showFooter ? styles.bodyScrollWithFooter : styles.bodyScroll}`}
      >
        {displayPageNum && (
          <p className={styles.pageNum}>{currentPage?.pageNumber ?? "1"}</p>
        )}

        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {displayPageTitle && (
              <>
                <Label size="allcaps" component="span">
                  {intl.formatMessage(messages.titleLabel)}
                </Label>
                <Heading size="xxs">{currentPage?.pageTitle || ""}</Heading>
              </>
            )}
          </div>
          <div className={styles.headerRight}>
            {displayDate && (
              <>
                <Label size="allcaps" component="span">
                  {intl.formatMessage(messages.dateLabel)}
                </Label>
                <Heading size="xxs">{new Date().toLocaleDateString()}</Heading>
              </>
            )}
          </div>
        </div>

        {currentPage?.enablePrevButton && currentPageIndex !== 0 && (
          <div
            role="button"
            className={styles.prevButton}
            tabIndex={0}
            onClick={handlePagePrevious}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handlePagePrevious();
            }}
          >
            <img
              src={notebookNextButton}
              alt={intl.formatMessage(messages.previousPage)}
            />
          </div>
        )}

        {currentPage?.enableNextButton &&
          currentPageIndex !== (pages?.length || 0) - 1 && (
            <div
              role="button"
              className={styles.nextButton}
              tabIndex={0}
              onClick={handlePageNext}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handlePageNext();
              }}
            >
              <img
                src={notebookNextButton}
                alt={intl.formatMessage(messages.nextPage)}
              />
            </div>
          )}

        <div
          ref={scrollRef}
          className={`${styles.scrollContent} ${
            showNotebookGridBody
              ? manualSize === "small"
                ? styles.scrollContentGridSmall
                : manualSize === "medium"
                  ? styles.scrollContentGridMedium
                  : styles.scrollContentGrid
              : ""
          }`}
          style={{
            backgroundImage: showNotebookGridBody
              ? `url(${notebookGrid})`
              : "none",
          }}
        >
          <Content components={currentPage?.components || []} />
        </div>
      </div>
      {showFooter && (
        <img src={ManualFooter} className={styles.footer} alt="" />
      )}
    </div>
  );
}

export const NotebookManual = (props: NotebookManualProps) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <NotebookManualInternal {...props} />
  </TranslationsProvider>
);
